import React from "react";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
// import { useField } from "formik";
// import languagesAvailable from "functions/getAllLanguagesSuported";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { EmailDataDefaultValues } from "customHooks/useValuesFromSource";
import { apiDirectCall, useApiCache } from "customHooks/useApi";
import { useAuthState } from "_ReactContext/AuthContext";
import { getTemplatesFromEmailDataDB } from "./EmailDataHelper";
import { createLocalUrlFromS3 } from "../File/File";
import useShowMessageFullControl from "customHooks/useShowMessageFullControl";
import { useFormContext } from "react-hook-form";

interface IButtonLoadTemplate {
    fieldValue: any; // Keep as any for compatibility with existing code
    setFieldValue: (p: any) => void; // Keep as any for compatibility
    fieldName: string;
    t: any; // Keep as any for i18n compatibility
    type?: string;
    arrayPlaceHolderBody: string[];
    arrayPlaceHolderSubject: string[];
    buttonPositionEnd?: boolean;
    splitTemplatesByLanguage: boolean;
}

interface TemplateOption {
    id: string;
    label: string;
    language?: string;
}

const ButtonLoadTemplate = (props: IButtonLoadTemplate) => {
    const {
        t,
        fieldValue,
        fieldName,
        type,
        buttonPositionEnd = true,
        arrayPlaceHolderSubject = [],
        arrayPlaceHolderBody = [],
        splitTemplatesByLanguage
    } = props;
    const { company } = useAuthState();
    const showMessageFullControl = useShowMessageFullControl();
    const { reset, getValues } = useFormContext();

    const postAddressSearch = `/emailtemplate/list`;

    const [getScheduleTemplateResult] = useApiCache(
        postAddressSearch,
        "post",
        { companyId: company.companyId, pageSize: 5000 },
        false
    );
    const templateList = getTemplatesFromEmailDataDB(
        getScheduleTemplateResult,
        splitTemplatesByLanguage,
        t,
        type
    );

    const handleTemplateSelection = async (
        event: React.ChangeEvent<{}>,
        template: TemplateOption | null
    ) => {
        if (!template) return;

        showMessageFullControl.openMessage({
            message: t("COMPLEOGENERAL_MessageUpdating"),
            variant: "info",
            showLinearProgress: true
        });

        const templateData = templateList.filter(
            (item) =>
                item.id === template.id &&
                (!template.language ||
                    item.items.filter((i) => i.language === template.language)
                        .length > 0)
        )[0];

        const templateLanguages = templateData.items.map(
            (item) => item.language
        );

        const currentValue = [...fieldValue];
        const currentFieldLanguages = currentValue.map((item) => item.language);

        for (let index = 0; index < currentValue.length; index++) {
            const element = currentValue[index];
            const languageData = templateData.items.filter((item) => {
                if (!template.language) {
                    return item.language === element.language;
                } else {
                    return true;
                }
            })[0];

            if (languageData) {
                const {
                    body,
                    subject,
                    attachments
                } = await getDataFromTemplate(
                    languageData.id,
                    company.companyId
                );

                currentValue[index]._tempEmailData = {
                    ...(currentValue[index]._tempEmailData || {}),
                    body: body,
                    subject: subject,
                    attachments: attachments
                };
                if (template.language) {
                    break;
                }
            }
        }
        if (!template.language) {
            const languagesToInclude = templateLanguages.filter(
                (item) => !currentFieldLanguages.includes(item)
            );
            for (const item of languagesToInclude) {
                const languageData = templateData.items.filter(
                    (itemLanguage) => itemLanguage.language === item
                )[0];
                if (languageData) {
                    const {
                        body,
                        subject,
                        attachments
                    } = await getDataFromTemplate(
                        languageData.id,
                        company.companyId
                    );

                    currentValue.push({
                        ...EmailDataDefaultValues,
                        default: false,
                        _tempEmailData: {
                            ...EmailDataDefaultValues._tempEmailData,
                            body: body,
                            subject: subject,
                            attachments: attachments,
                            arrayPlaceHolderBody: arrayPlaceHolderBody,
                            arrayPlaceHolderSubject: arrayPlaceHolderSubject
                        },
                        language: languageData.language
                    });
                }
            }
        }

        // setFieldValue(currentValue);
        reset({ ...getValues(), [fieldName]: currentValue });

        showMessageFullControl.closeMessage();
    };

    // Prepare template options for Autocomplete
    const templateOptions = templateList.map((option) => {
        const langParam = splitTemplatesByLanguage
            ? option.items[0].language
            : undefined;
        return {
            id: option.id,
            label: option.label,
            language: langParam
        };
    });

    if (templateList.length) {
        return (
            <Grid
                container
                item
                alignItems={buttonPositionEnd ? "flex-end" : "flex-start"}
                justify={buttonPositionEnd ? "flex-end" : "flex-start"}
                alignContent="center"
            >
                <Grid item xs={12} md={6} style={{ width: "100%" }}>
                    <Autocomplete
                        options={templateOptions}
                        getOptionLabel={(option) => option.label}
                        onChange={handleTemplateSelection}
                        fullWidth
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t(
                                    "SCHEDULEDATAFIELD_textChooseTemplate"
                                )}
                                variant="standard"
                                size="small"
                                placeholder={t(
                                    "SCHEDULEDATAFIELD_textChooseTemplate"
                                )}
                            />
                        )}
                    />
                </Grid>
                {showMessageFullControl.MessageElement}
            </Grid>
        );
    } else {
        return null;
    }
};

const getDataFromTemplate = async (id: string, companyId: number) => {
    const dataReturn = await apiDirectCall("/email/getemail", "post", {
        id: id,
        companyId: companyId
    });
    const mainData = dataReturn?.data?.data;
    const body = mainData?.body;
    const subject = mainData?.subject;
    const attachments = mainData?.attachments || [];

    const attachmentsData: Compleo.IObject[] = [];
    for (const attachment of attachments) {
        const fileData = await createLocalUrlFromS3(attachment.key);
        const file = new File([fileData.blob], attachment.originalFileName);

        attachmentsData.push({
            tags: [],
            FileInfo: file,
            originalFileName: file.name
        });
    }
    return { body, subject, attachments: attachmentsData };
};

export default ButtonLoadTemplate;
